import { useState, useEffect } from 'react';

const useTableFEHook = ({
  defaultPageRequest = {
    pageNumber: 1,
    pageSize: 0,
    sortColumn: 'createdAt',
    sortDirection: 'desc',
    filters: [],
  },
}) => {
  const [pageRequest, setPageRequest] = useState(defaultPageRequest);
  const [columnName, setColumnName] = useState('');
  const [filter, setFilter] = useState({});
  const [activeRows, setActiveRows] = useState([]);

  // Apply Filter
  const applyFilter = colFilters => {
    // Set Up Selected Filter Object with key as column name and value as selected filters
    const selectedFilterObject = {
      key: columnName,
      values: colFilters,
    };
    // Check if the selected filter object key already exists in the filters array
    const existingFilterIndex = pageRequest.filters.findIndex(
      colFilter => colFilter.key === columnName,
    );
    let updatedFilters;
    // If it exists, update the values
    if (existingFilterIndex !== -1) {
      updatedFilters = [...pageRequest.filters];
    } else {
      // If it doesn't exist, add the new filter object to the filters array
      updatedFilters = [...pageRequest.filters, selectedFilterObject];
      setPageRequest(prev => ({
        ...prev,
        filters: updatedFilters,
      }));
    }
    setColumnName('');
  };

  // Reset Active Rows
  useEffect(() => {
    setActiveRows([]);
  }, [pageRequest]);

  // Handle Row Check
  const handleRowCheck = (id, checked) => {
    if (checked) {
      setActiveRows(prev => [...prev, id]);
    } else {
      setActiveRows(prev => prev.filter(row => row !== id));
    }
  };

  // Select All Rows or remove All Rows
  const selectAllRows = (data, isChecked, isIndex = true, idKey = null) => {
    const dataArray = data || [];
    if (isChecked) {
      if (!isIndex) {
        if (dataArray?.length > 0) {
          const ids = [];
          dataArray.forEach(item => {
            if (idKey) {
              ids.push(item?.[idKey || 'id']);
            }
          });
          setActiveRows(ids);
        }
      } else if (dataArray?.length > 0) {
        const ids = [];
        dataArray.forEach((item, index) => {
          ids.push(index);
        });
        setActiveRows(ids);
      }
    } else {
      setActiveRows([]);
    }
  };

  // Reset all active rows
  const resetActiveRows = () => {
    setActiveRows([]);
  };

  // Reset Filters
  const resetFilters = () => {
    setPageRequest(prev => ({
      ...prev,
      filters: [],
    }));
  };

  // Handle Manual sort
  const handleManualSort = (selectedColumnName, sortDirection) => {
    setPageRequest(prev => ({
      ...prev,
      sortColumn: selectedColumnName,
      sortDirection,
    }));
  };

  // Clear single Filter if exists
  const clearSingleFilter = colName => {
    const updatedFilters = pageRequest.filters.filter(
      singleFilter => singleFilter.key !== colName,
    );
    setPageRequest(prev => ({
      ...prev,
      filters: updatedFilters,
    }));
  };

  // Clear single filter value from a existing filter
  const clearSingleFilterValue = (colName, value) => {
    setPageRequest(prev => ({
      ...prev,
      filters: prev.filters.reduce((acc, singleFilter) => {
        if (singleFilter.key !== colName) {
          acc.push(singleFilter);
        } else {
          const newVals = singleFilter.values.filter(
            singleValue => singleValue !== value,
          );
          if (newVals.length) {
            acc.push({ ...singleFilter, values: newVals });
          }
        }
        return acc;
      }, []),
    }));
  };

  // Check if the column is sorted. If yes, return "ASC" or "DESC" else ""
  const checkColumnIfSorted = colName => {
    if (pageRequest.sortColumn === colName) {
      return pageRequest.sortDirection === 'ASC' ? 'ASC' : 'DESC';
    }
    return '';
  };

  const handleAutoSort = colName => {
    const currentSortDirection = checkColumnIfSorted(colName);
    const newSortDirection = currentSortDirection === 'ASC' ? 'DESC' : 'ASC';
    handleManualSort(colName, newSortDirection);
  };

  return {
    pageRequest,
    setPageRequest,
    applyFilter,
    filter,
    setFilter,
    activeRows,
    handleRowCheck,
    selectAllRows,
    resetFilters,
    resetActiveRows,
    handleManualSort,
    clearSingleFilter,
    clearSingleFilterValue,
    checkColumnIfSorted,
    columnName,
    setColumnName,
    handleAutoSort,
  };
};

export default useTableFEHook;
